import { storableError } from '../../util/errors';
import {
  getSavedSearches,
  updateSavedSearch,
  deleteSavedSearch,
  markSavedSearchesOpened,
  getSavedSearchNewCounts,
} from '../../util/api';

// ================ Action types ================ //

export const FETCH_SAVED_SEARCHES_REQUEST = 'app/SavedSearchesPage/FETCH_SAVED_SEARCHES_REQUEST';
export const FETCH_SAVED_SEARCHES_SUCCESS = 'app/SavedSearchesPage/FETCH_SAVED_SEARCHES_SUCCESS';
export const FETCH_SAVED_SEARCHES_ERROR = 'app/SavedSearchesPage/FETCH_SAVED_SEARCHES_ERROR';

export const FETCH_NEW_COUNTS_SUCCESS = 'app/SavedSearchesPage/FETCH_NEW_COUNTS_SUCCESS';

export const UPDATE_SAVED_SEARCH_REQUEST = 'app/SavedSearchesPage/UPDATE_SAVED_SEARCH_REQUEST';
export const UPDATE_SAVED_SEARCH_SUCCESS = 'app/SavedSearchesPage/UPDATE_SAVED_SEARCH_SUCCESS';
export const UPDATE_SAVED_SEARCH_ERROR = 'app/SavedSearchesPage/UPDATE_SAVED_SEARCH_ERROR';

export const MARK_SEARCH_SAVED = 'app/SavedSearchesPage/MARK_SEARCH_SAVED';

export const DELETE_SAVED_SEARCH_REQUEST = 'app/SavedSearchesPage/DELETE_SAVED_SEARCH_REQUEST';
export const DELETE_SAVED_SEARCH_SUCCESS = 'app/SavedSearchesPage/DELETE_SAVED_SEARCH_SUCCESS';
export const DELETE_SAVED_SEARCH_ERROR = 'app/SavedSearchesPage/DELETE_SAVED_SEARCH_ERROR';

// ================ Reducer ================ //

const initialState = {
  savedSearches: [],
  fetchInProgress: false,
  fetchError: null,

  // Per-row pending ids, so pressing pause on one row does not grey out the
  // whole list. Both are arrays of saved-search ids.
  updatingIds: [],
  deletingIds: [],
  mutationError: null,

  // "N nya" badge. `newCountsAvailable` stays false until the notification
  // engine exists to write the numbers (BR-7) — see server getNewCounts.js.
  newCounts: {},
  newCountsAvailable: false,

  /**
   * Query strings saved during this session, so every copy of the save button
   * shows the same thing.
   *
   * The search page renders that button five times — the quick-search card and
   * the results toolbar each have a desktop and a hidden mobile copy, plus the
   * no-results block. They hold their own local status, so without this a user
   * who saves from the sidebar still sees "Bevaka denna sökning" in the
   * toolbar. It lives in Redux rather than a module variable because only a
   * store update re-renders the other four.
   *
   * A label only. The database decides what is really saved, via the unique
   * index; this is cleared on delete so it cannot claim a bevakning that is
   * gone.
   */
  savedParams: [],
};

const withoutId = (ids, id) => ids.filter(x => x !== id);

export default function savedSearchesPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case FETCH_SAVED_SEARCHES_REQUEST:
      return { ...state, fetchInProgress: true, fetchError: null };
    case FETCH_SAVED_SEARCHES_SUCCESS:
      return { ...state, fetchInProgress: false, savedSearches: payload };
    case FETCH_SAVED_SEARCHES_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case FETCH_NEW_COUNTS_SUCCESS:
      return { ...state, newCounts: payload.counts, newCountsAvailable: payload.available };

    case UPDATE_SAVED_SEARCH_REQUEST:
      return { ...state, updatingIds: [...state.updatingIds, payload], mutationError: null };
    case UPDATE_SAVED_SEARCH_SUCCESS:
      return {
        ...state,
        updatingIds: withoutId(state.updatingIds, payload._id),
        savedSearches: state.savedSearches.map(s => (s._id === payload._id ? payload : s)),
      };
    case UPDATE_SAVED_SEARCH_ERROR:
      return {
        ...state,
        updatingIds: withoutId(state.updatingIds, payload.id),
        mutationError: payload.error,
      };

    case DELETE_SAVED_SEARCH_REQUEST:
      return { ...state, deletingIds: [...state.deletingIds, payload], mutationError: null };
    case MARK_SEARCH_SAVED:
      return state.savedParams.includes(payload)
        ? state
        : { ...state, savedParams: [...state.savedParams, payload] };

    case DELETE_SAVED_SEARCH_SUCCESS:
      return {
        ...state,
        deletingIds: withoutId(state.deletingIds, payload),
        savedSearches: state.savedSearches.filter(s => s._id !== payload),
        // Deleting makes the save buttons' "already saved" labels wrong.
        savedParams: [],
      };
    case DELETE_SAVED_SEARCH_ERROR:
      return {
        ...state,
        deletingIds: withoutId(state.deletingIds, payload.id),
        mutationError: payload.error,
      };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchSavedSearchesRequest = () => ({ type: FETCH_SAVED_SEARCHES_REQUEST });
export const fetchSavedSearchesSuccess = savedSearches => ({
  type: FETCH_SAVED_SEARCHES_SUCCESS,
  payload: savedSearches,
});
export const fetchSavedSearchesError = e => ({
  type: FETCH_SAVED_SEARCHES_ERROR,
  payload: e,
  error: true,
});

export const fetchNewCountsSuccess = payload => ({ type: FETCH_NEW_COUNTS_SUCCESS, payload });

export const updateSavedSearchRequest = id => ({ type: UPDATE_SAVED_SEARCH_REQUEST, payload: id });
export const updateSavedSearchSuccess = savedSearch => ({
  type: UPDATE_SAVED_SEARCH_SUCCESS,
  payload: savedSearch,
});
export const updateSavedSearchError = (id, error) => ({
  type: UPDATE_SAVED_SEARCH_ERROR,
  payload: { id, error },
  error: true,
});

export const markSearchSaved = params => ({ type: MARK_SEARCH_SAVED, payload: params });

export const deleteSavedSearchRequest = id => ({ type: DELETE_SAVED_SEARCH_REQUEST, payload: id });
export const deleteSavedSearchSuccess = id => ({ type: DELETE_SAVED_SEARCH_SUCCESS, payload: id });
export const deleteSavedSearchError = (id, error) => ({
  type: DELETE_SAVED_SEARCH_ERROR,
  payload: { id, error },
  error: true,
});

// ================ Thunks ================ //

export const fetchSavedSearches = () => dispatch => {
  dispatch(fetchSavedSearchesRequest());

  return getSavedSearches()
    .then(response => {
      dispatch(fetchSavedSearchesSuccess(response.savedSearches || []));
      return response;
    })
    .catch(e => {
      dispatch(fetchSavedSearchesError(storableError(e)));
      throw e;
    });
};

export const updateSearch = (id, changes) => dispatch => {
  dispatch(updateSavedSearchRequest(id));

  return updateSavedSearch(id, changes)
    .then(response => {
      dispatch(updateSavedSearchSuccess(response.savedSearch));
      return response;
    })
    .catch(e => {
      dispatch(updateSavedSearchError(id, storableError(e)));
    });
};

export const removeSearch = id => dispatch => {
  dispatch(deleteSavedSearchRequest(id));

  return deleteSavedSearch(id)
    .then(response => {
      // DELETE_SAVED_SEARCH_SUCCESS also empties `savedParams`, so the search
      // page's save buttons stop claiming a bevakning that no longer exists.
      dispatch(deleteSavedSearchSuccess(id));
      return response;
    })
    .catch(e => {
      dispatch(deleteSavedSearchError(id, storableError(e)));
    });
};

export const loadData = () => dispatch => {
  // Order matters: read the counts against the OLD lastOpenedAt, then move the
  // marker. Marking first would always report zero new matches.
  return dispatch(fetchSavedSearches())
    .then(() => getSavedSearchNewCounts())
    .then(response => {
      dispatch(
        fetchNewCountsSuccess({
          counts: response.counts || {},
          available: !!response.available,
        })
      );
      return markSavedSearchesOpened();
    })
    .catch(() => {
      // The list itself already reported its own failure. A badge or a
      // read-marker that does not update must not blank the page.
      return null;
    });
};
